import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { invalidateMonths, invalidateWeeks } from "@/hooks/use-time-entries";
import { timeKeys } from "@/lib/query";
import { ApiError, getJson, sendJson } from "@/lib/time/api-client";
import type {
  RevokeSignOffInput,
  SignOffRevokeResult,
  SignOffSubmitResult,
  SignOffView,
  SubmitSignOffInput,
} from "@/schemas/time";

/** The server creates missing sign-off rows (at most once a day per person) before it answers. */
export async function loadSignOffs(): Promise<SignOffView> {
  return getJson<SignOffView>("/api/time/signoffs");
}

export function useSignOffs() {
  // One retry, so a failed load shows its error (and the plain "Sign off") sooner.
  return useQuery({ queryKey: timeKeys.signOffs(), queryFn: loadSignOffs, retry: 1 });
}

/** A sign-off changes the sign-off list, the weeks it locks, and Home's month totals. */
export function invalidateAfterSignOff(queryClient: QueryClient) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: timeKeys.signOffs() }),
    invalidateWeeks(queryClient),
    invalidateMonths(queryClient),
  ]);
}

export function useSubmitSignOff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SubmitSignOffInput) =>
      sendJson<SignOffSubmitResult>("POST", "/api/time/signoffs/submit", input),
    onSuccess: async (result) => {
      toast.success(result.isLate ? "Signed off (late)" : "Signed off", { id: "time-signoff" });
      await invalidateAfterSignOff(queryClient);
    },
    onError: (err) => toast.error(err.message),
    retry: false,
  });
}

/** TE-46: the browser sends only the row id; the server knows whose it is. */
export function sendRevoke(input: RevokeSignOffInput): Promise<SignOffRevokeResult> {
  return sendJson<SignOffRevokeResult>("POST", "/api/time/signoffs/revoke", input);
}

/** The server's answer when a write may or may not have landed. */
const MAY_HAVE_SAVED = 504;

/** A revoke that may have saved can have reopened the week, so it refreshes like a success. */
export async function invalidateAfterRevokeError(queryClient: QueryClient, err: Error) {
  if (err instanceof ApiError && err.status === MAY_HAVE_SAVED) {
    await invalidateAfterSignOff(queryClient);
  }
}

/** Revoking reopens a week or month: the list, the week's locks and Home's totals all change. */
export function useRevokeSignOff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: sendRevoke,
    onSuccess: async () => {
      toast.success("Sign-off revoked", { id: "time-signoff" });
      await invalidateAfterSignOff(queryClient);
    },
    onError: async (err) => {
      toast.error(err.message);
      await invalidateAfterRevokeError(queryClient, err);
    },
    retry: false,
  });
}

export type RevokeStep = "revoked" | "failed" | "not-run";

/** What happened to each half of a week-and-month revoke; `error` is the first failure. */
export interface PairRevokeResult {
  month: RevokeStep;
  week: RevokeStep;
  error: Error | null;
}

const asError = (err: unknown): Error =>
  err instanceof Error ? err : new Error("Something went wrong. Try again.");

/**
 * Controller decision 2026-10-16: a week whose month is signed off is revoked with its month.
 * Month first (so the week never sits reopened under a locked month), then the week. Each is
 * its own call, audited and settled (504) on its own; a month that fails, or may not have
 * landed, stops before the week.
 */
export async function revokeMonthThenWeek(
  ids: { monthId: string; weekId: string },
  send: (input: RevokeSignOffInput) => Promise<SignOffRevokeResult> = sendRevoke,
): Promise<PairRevokeResult> {
  try {
    await send({ signOffId: ids.monthId });
  } catch (err) {
    return { month: "failed", week: "not-run", error: asError(err) };
  }
  try {
    await send({ signOffId: ids.weekId });
  } catch (err) {
    return { month: "revoked", week: "failed", error: asError(err) };
  }
  return { month: "revoked", week: "revoked", error: null };
}

/** Something landed, or may have (504): sign-offs, weeks and Home re-read the real state. */
export function pairNeedsRefresh(result: PairRevokeResult): boolean {
  if (result.month === "revoked") return true;
  return result.error instanceof ApiError && result.error.status === MAY_HAVE_SAVED;
}

/** Revoke a week and its signed-off month (month first). Resolves with each half's outcome. */
export function useRevokeWeekAndMonth() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: { monthId: string; weekId: string }) => revokeMonthThenWeek(ids),
    onSuccess: async (result) => {
      if (result.error === null) {
        toast.success("Week and month revoked", { id: "time-signoff" });
      } else if (result.month === "revoked") {
        toast.error(`Month revoked. Week not revoked: ${result.error.message}`, {
          id: "time-signoff",
        });
      } else {
        toast.error(result.error.message, { id: "time-signoff" });
      }
      if (pairNeedsRefresh(result)) await invalidateAfterSignOff(queryClient);
    },
    retry: false,
  });
}
